import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { loginApi } from '../../api/auth.api';
import useAuthStore from '../../store/useAuthStore';

/* ─── Inline styles / keyframes injected once ─── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@300;500;700;800&family=Barlow:wght@300;400;500&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --bg:        #0a0c0f;
      --surface:   #111418;
      --border:    #222730;
      --border-hi: #2e3540;
      --accent:    #f59e0b;
      --accent-2:  #ef4444;
      --text-1:    #f0f2f5;
      --text-2:    #8a9ab0;
      --text-3:    #4a5568;
      --input-bg:  #161b22;
      --radius:    4px;
    }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes lineGrow {
      from { width: 0; }
      to   { width: 48px; }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.4; }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes scanline {
      0%   { top: -4px; }
      100% { top: 100%; }
    }
    @keyframes shimmer {
      0%   { background-position: -400px 0; }
      100% { background-position: 400px 0; }
    }

    .login-root {
      font-family: 'Barlow', sans-serif;
      display: flex;
      min-height: 100vh;
      background: var(--bg);
      color: var(--text-1);
      overflow: hidden;
    }

    /* ── Left panel ── */
    .lp-left {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 48px 56px;
      border-right: 1px solid var(--border);
      position: relative;
      overflow: hidden;
    }
    .lp-left::before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        repeating-linear-gradient(0deg, transparent, transparent 39px, var(--border) 39px, var(--border) 40px),
        repeating-linear-gradient(90deg, transparent, transparent 39px, var(--border) 39px, var(--border) 40px);
      opacity: 0.35;
      pointer-events: none;
    }
    .lp-left::after {
      content: '';
      position: absolute;
      left: -120px; bottom: -120px;
      width: 480px; height: 480px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(245,158,11,0.12) 0%, transparent 70%);
      pointer-events: none;
    }
    .lp-scanline {
      position: absolute;
      left: 0; right: 0;
      height: 4px;
      background: linear-gradient(to bottom, transparent, rgba(245,158,11,0.15), transparent);
      animation: scanline 6s linear infinite;
      pointer-events: none;
    }

    .lp-brand { position: relative; z-index: 1; animation: fadeUp 0.6s ease both; }
    .lp-icon {
      display: inline-flex; align-items: center; justify-content: center;
      width: 52px; height: 52px;
      border: 1px solid var(--accent);
      border-radius: var(--radius);
      font-size: 22px;
      margin-bottom: 20px;
      box-shadow: 0 0 20px rgba(245,158,11,0.2), inset 0 0 10px rgba(245,158,11,0.05);
    }
    .lp-brand-name {
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 4px;
      text-transform: uppercase;
      color: var(--accent);
    }

    .lp-hero { position: relative; z-index: 1; animation: fadeUp 0.6s 0.1s ease both; }
    .lp-tagline {
      font-family: 'Barlow Condensed', sans-serif;
      font-size: clamp(42px, 5vw, 68px);
      font-weight: 800;
      line-height: 1.0;
      letter-spacing: -1px;
      text-transform: uppercase;
    }
    .lp-tagline span { color: var(--accent); }
    .lp-sub {
      margin-top: 20px;
      font-size: 14px;
      font-weight: 300;
      color: var(--text-2);
      line-height: 1.7;
      max-width: 320px;
    }
    .lp-accent-line {
      margin-top: 28px;
      height: 2px;
      background: var(--accent);
      animation: lineGrow 0.8s 0.5s ease both;
      animation-fill-mode: both;
      width: 48px;
    }

    .lp-stats {
      display: flex; gap: 40px;
      position: relative; z-index: 1;
      animation: fadeUp 0.6s 0.2s ease both;
    }
    .stat-item { }
    .stat-val {
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 28px;
      font-weight: 700;
      color: var(--text-1);
    }
    .stat-label {
      font-size: 11px;
      font-weight: 400;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: var(--text-3);
      margin-top: 2px;
    }

    /* ── Right panel (form) ── */
    .lp-right {
      width: 440px;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 56px 48px;
      position: relative;
      background: var(--surface);
    }

    .form-header { margin-bottom: 40px; animation: fadeUp 0.5s 0.15s ease both; }
    .form-eyebrow {
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 3px;
      text-transform: uppercase;
      color: var(--text-3);
      margin-bottom: 8px;
    }
    .form-title {
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 32px;
      font-weight: 700;
      letter-spacing: -0.5px;
      text-transform: uppercase;
    }
    .form-title span { color: var(--accent); }

    /* Field */
    .field { margin-bottom: 20px; animation: fadeUp 0.5s ease both; }
    .field:nth-child(1) { animation-delay: 0.2s; }
    .field:nth-child(2) { animation-delay: 0.25s; }
    .field-label {
      display: flex; align-items: center; gap: 6px;
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 2.5px;
      text-transform: uppercase;
      color: var(--text-3);
      margin-bottom: 8px;
    }
    .field-label-dot {
      width: 5px; height: 5px;
      background: var(--accent);
      border-radius: 50%;
    }
    .field-wrap {
      position: relative;
      display: flex;
      align-items: center;
      border: 1px solid var(--border);
      border-radius: var(--radius);
      background: var(--input-bg);
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    .field-wrap:focus-within {
      border-color: var(--accent);
      box-shadow: 0 0 0 3px rgba(245,158,11,0.08);
    }
    .field-wrap.error { border-color: var(--accent-2); }
    .field-icon {
      padding: 0 14px;
      color: var(--text-3);
      font-size: 15px;
      pointer-events: none;
    }
    .field-input {
      flex: 1;
      padding: 13px 0;
      font-family: 'Barlow', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: var(--text-1);
      background: transparent;
      border: none;
      outline: none;
      caret-color: var(--accent);
    }
    .field-input::placeholder { color: var(--text-3); font-weight: 300; }
    .field-input:-webkit-autofill,
    .field-input:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px var(--input-bg) inset !important;
      -webkit-text-fill-color: var(--text-1) !important;
    }
    .eye-btn {
      padding: 0 14px;
      background: none; border: none; cursor: pointer;
      color: var(--text-3);
      font-size: 14px;
      transition: color 0.2s;
    }
    .eye-btn:hover { color: var(--text-2); }
    .field-error {
      margin-top: 6px;
      font-size: 11px;
      color: var(--accent-2);
      letter-spacing: 0.5px;
    }

    /* Alert */
    .alert-error {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 12px 16px;
      border: 1px solid rgba(239,68,68,0.3);
      border-radius: var(--radius);
      background: rgba(239,68,68,0.06);
      color: #fca5a5;
      font-size: 13px;
      margin-bottom: 24px;
      animation: fadeUp 0.3s ease;
    }
    .alert-icon { font-size: 14px; flex-shrink: 0; margin-top: 1px; }

    /* Submit btn */
    .submit-btn {
      width: 100%;
      margin-top: 8px;
      padding: 14px;
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 3px;
      text-transform: uppercase;
      color: #000;
      background: var(--accent);
      border: none;
      border-radius: var(--radius);
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: transform 0.15s, box-shadow 0.15s;
      animation: fadeUp 0.5s 0.3s ease both;
    }
    .submit-btn::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%);
      background-size: 400px 100%;
      opacity: 0;
      transition: opacity 0.3s;
    }
    .submit-btn:hover:not(:disabled)::before { opacity: 1; animation: shimmer 0.8s linear; }
    .submit-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(245,158,11,0.35); }
    .submit-btn:active:not(:disabled) { transform: translateY(0); }
    .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

    .btn-inner { display: flex; align-items: center; justify-content: center; gap: 10px; }
    .spinner {
      width: 14px; height: 14px;
      border: 2px solid rgba(0,0,0,0.3);
      border-top-color: #000;
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
    }

    /* Footer note */
    .form-footer {
      margin-top: 32px;
      padding-top: 24px;
      border-top: 1px solid var(--border);
      display: flex; align-items: center; justify-content: space-between;
      animation: fadeUp 0.5s 0.35s ease both;
    }
    .footer-sys {
      font-size: 10px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      color: var(--text-3);
    }
    .status-dot {
      display: flex; align-items: center; gap: 6px;
      font-size: 10px; letter-spacing: 1px;
      text-transform: uppercase;
      color: var(--text-3);
    }
    .dot {
      width: 6px; height: 6px;
      background: #22c55e;
      border-radius: 50%;
      animation: pulse 2s ease-in-out infinite;
    }

    /* Responsive */
    @media (max-width: 768px) {
      .lp-left { display: none; }
      .lp-right { width: 100%; padding: 40px 28px; }
    }
  `}</style>
);

/* ─── Icons (inline SVG, no deps) ─── */
const IconUser = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
  </svg>
);
const IconLock = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="5" y="11" width="14" height="10" rx="2"/>
    <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
  </svg>
);
const IconEye = ({ off }) => off ? (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
) : (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconAlert = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);
const IconArrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

/* ─── Main Component ─── */
const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { loginID: '', password: '' }
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const response = await loginApi(data);
      const { token, ...user } = response;
      setAuth(token, user);
      const hasAdminAccess = user.moduleList?.some(m => m.moduleId === 8);
      navigate(hasAdminAccess ? '/admin/users' : '/dashboard', { replace: true });
    } catch (err) {
      setErrorMsg(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <GlobalStyles />
      <div className="login-root">

        {/* ── Left Panel ── */}
        <div className="lp-left">
          <div className="lp-scanline" />

          <div className="lp-brand">
            <div className="lp-icon">🏭</div>
            <div className="lp-brand-name">Manufacturing IMS</div>
          </div>

          <div className="lp-hero">
            <h1 className="lp-tagline">
              SMART<br />
              PRODUCTION<br />
              <span>CONTROL</span>
            </h1>
            <p className="lp-sub">
              Real-time inventory intelligence, automated workflows,
              and precision analytics — built for the factory floor.
            </p>
            <div className="lp-accent-line" />
          </div>

          <div className="lp-stats">
            {[['99.8%', 'Uptime SLA'], ['< 0.3s', 'Avg. Response'], ['ISO 9001', 'Certified']].map(([val, lbl]) => (
              <div className="stat-item" key={lbl}>
                <div className="stat-val">{val}</div>
                <div className="stat-label">{lbl}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right Panel ── */}
        <div className="lp-right">
          <div className="form-header">
            <div className="form-eyebrow">Secure Access Portal</div>
            <h2 className="form-title">Sign <span>In</span></h2>
          </div>

          {errorMsg && (
            <div className="alert-error">
              <span className="alert-icon"><IconAlert /></span>
              <span>{errorMsg}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Login ID */}
            <div className="field">
              <label className="field-label">
                <span className="field-label-dot" />
                Login ID
              </label>
              <Controller
                name="loginID"
                control={control}
                rules={{ required: 'Login ID is required' }}
                render={({ field }) => (
                  <div className={`field-wrap${errors.loginID ? ' error' : ''}`}>
                    <span className="field-icon"><IconUser /></span>
                    <input
                      {...field}
                      className="field-input"
                      type="text"
                      placeholder="Enter your login ID"
                      autoFocus
                      autoComplete="username"
                    />
                  </div>
                )}
              />
              {errors.loginID && <div className="field-error">⚠ {errors.loginID.message}</div>}
            </div>

            {/* Password */}
            <div className="field">
              <label className="field-label">
                <span className="field-label-dot" />
                Password
              </label>
              <Controller
                name="password"
                control={control}
                rules={{ required: 'Password is required' }}
                render={({ field }) => (
                  <div className={`field-wrap${errors.password ? ' error' : ''}`}>
                    <span className="field-icon"><IconLock /></span>
                    <input
                      {...field}
                      className="field-input"
                      type={showPwd ? 'text' : 'password'}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowPwd(v => !v)}
                      aria-label={showPwd ? 'Hide password' : 'Show password'}
                    >
                      <IconEye off={showPwd} />
                    </button>
                  </div>
                )}
              />
              {errors.password && <div className="field-error">⚠ {errors.password.message}</div>}
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              <span className="btn-inner">
                {loading ? (
                  <><span className="spinner" /> Authenticating…</>
                ) : (
                  <>Authenticate <IconArrow /></>
                )}
              </span>
            </button>
          </form>

          <div className="form-footer">
            <span className="footer-sys">IMS v3.4.1</span>
            <span className="status-dot"><span className="dot" /> Systems Online</span>
          </div>
        </div>

      </div>
    </>
  );
};

export default LoginPage;